import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

/** The selection after toggling one id: added when absent, removed when present. */
export function toggleId(selected: number[], id: number): number[] {
  const current = selected || [];
  return current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
}

/**
 * Dropdown of checkboxes to pick several values of a list filter.
 * An empty selection means "no filter". Emits the selected ids on each change.
 */
@Component({
  standalone: true,
  selector: 'app-multi-select',
  templateUrl: 'multi-select.component.html',
  imports: [CommonModule],
  styles: [
    `
      .ms {
        display: inline-block;
        position: relative;
        margin-right: 10px;
        margin-bottom: 5px;
        vertical-align: middle;
      }
      .ms-button {
        width: 100%;
        text-align: left;
        background: white;
        border-top: 2px solid #555;
        border-left: 2px solid #555;
        border-right: 2px solid #ccc;
        border-bottom: 2px solid #ccc;
        padding: 1px 22px 1px 4px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        position: relative;
      }
      .ms-button .ms-placeholder {
        color: #555;
      }
      .ms-button .ms-caret {
        position: absolute;
        right: 6px;
        top: 50%;
        margin-top: -2px;
      }
      .ms-list {
        position: absolute;
        top: 100%;
        left: 0;
        width: max-content;
        min-width: 100%;
        max-width: 500px;
        margin: 0;
        padding: 0;
        list-style: none;
        background: white;
        border: 1px solid #ccc;
        border-top: none;
        max-height: 260px;
        overflow-y: auto;
        z-index: 1000;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
      }
      .ms-list label {
        display: block;
        margin: 0;
        padding: 6px 12px;
        font-weight: normal;
        cursor: pointer;
        white-space: nowrap;
        border-bottom: 1px solid #eee;
      }
      .ms-list label:hover {
        background-color: #f5f5f5;
      }
      .ms-list input {
        margin: 0 8px 0 0;
      }
    `,
  ],
})
export class MultiSelectComponent<T = any> {
  @Input() placeholder = '';
  @Input() items: T[] = [];
  @Input() label: (item: T) => string = (item: any) => String(item);
  @Input() idOf: (item: T) => number = (item: any) => item.id;
  @Input() selected: number[] = [];
  @Input() width = '210px';
  @Output() selectedChange = new EventEmitter<number[]>();

  open = false;

  constructor(private host: ElementRef) {}

  isSelected(item: T): boolean {
    return (this.selected || []).includes(this.idOf(item));
  }

  /** Labels of the selected items, in list order, for the closed button. */
  get summary(): string {
    return (this.items || [])
      .filter((item) => this.isSelected(item))
      .map((item) => this.label(item))
      .join(', ');
  }

  toggle(item: T): void {
    this.selected = toggleId(this.selected, this.idOf(item));
    this.selectedChange.emit(this.selected);
  }

  @HostListener('document:click', ['$event'])
  closeOnOutsideClick(event: Event): void {
    if (this.open && !this.host.nativeElement.contains(event.target)) {
      this.open = false;
    }
  }

  @HostListener('keydown.escape')
  close(): void {
    this.open = false;
  }
}
